import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useAdminAuth } from "@/contexts/AdminAuthContext";

interface AdminRouteProps {
  children: React.ReactNode;
}

export default function AdminRoute({ children }: AdminRouteProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [, setLocation] = useLocation();
  const { setAdminAuthenticated } = useAdminAuth();

  useEffect(() => {
    const checkAuth = async () => {
      // Extract org slug from URL first (works in new tabs/bookmarks: /org/:orgSlug/admin)
      const urlMatch = window.location.pathname.match(/^\/org\/([^/]+)\//);
      const orgSlug = urlMatch?.[1] ?? sessionStorage.getItem("currentOrgSlug");
      if (!orgSlug) {
        setIsAuthenticated(false);
        setLocation("/");
        return;
      }
      // Keep sessionStorage in sync so other code that reads it stays consistent
      sessionStorage.setItem("currentOrgSlug", orgSlug);

      try {
        const res = await fetch(`/org/${orgSlug}/api/admin/verify-session`, {
          method: "POST",
          credentials: "include",
        });

        if (res.ok) {
          const data = await res.json();
          if (data.valid) {
            // Refresh user data from server
            sessionStorage.setItem("adminUser", JSON.stringify(data.user));
            setAdminAuthenticated(true);
            setIsAuthenticated(true);
            return;
          }
        }
      } catch {
        // Network error — fall through to redirect
      }

      // Session invalid — clear UI state and redirect
      sessionStorage.removeItem("adminUser");
      sessionStorage.removeItem("currentOrgSlug");
      sessionStorage.removeItem("orgBranding");
      setAdminAuthenticated(false);
      setIsAuthenticated(false);
      setLocation("/");
    };

    checkAuth();
  }, [setLocation]);

  // Show loading state while checking authentication
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-google-blue mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <>{children}</>;
  }

  return null;
}
